import React, { Fragment, useEffect, useState, createContext, useMemo } from 'react';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { arrayOf, func, object, string } from 'prop-types';
import {
  Button,
  Grid,
  Link,
  withStyles,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { cloneDeep, findIndex, get, uniq } from 'lodash';

const CATEGORY_ORGANIZATIONS_TAB = 'orgs-tab';

import {
  FilterByName,
  FilterBySeparator,
  HeaderActionButtons,
} from './controls';
import styles from './styles';
import {
  Switch,
  TagsGroup,
  FormActionButtons,
} from '../../../../components';
import ListContainer from '../../../list';
import {
  GRID_ROWS_PER_PAGE_OPTIONS,
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ASSIGNED,
  NOT_ASSIGNED,
  ALERT_SUCCESS,
  ALERT_ERROR,
 } from '../../../../constants';
import {
  fetchCategoryAccessOrgs,
  updateCategoryOrgAccess,
} from '../../../../actions/templateCategory';
import {
  getCategoryOrgs,
  getCategoryOrgsUpdateSuccess,
  getValidationErrors,
} from '../../../../reducers/templateCategory';
import { getI18n } from '../../../../utils/intl';
import FilterByTag from '../../../Organization/List/controls/filterByTag';
import { getConfig } from '../../../../reducers/portalConfig';
import { getFirstValidationError } from '../../../../utils';

export const CategoryOrgListContext = createContext();
const CHANGE_MODE_REMOVE_ACCESS = 'ASSIGNED';
const CHANGE_MODE_ADD_ACCESS = 'NOT_ASSIGNED';

export const CategoryOrganizationAccess = (props) => {
  const {
    classes,
    fetchCategoryAccessOrgs,
    isUpdateSuccess,
    notifyMessages,
    categoryUuid,
    categoryOrgs,
    currentTab,
    updateCategoryOrgAccess,
    config,
    handleOrgsChange,
    validationErrors,
  } = props;

  const { getI18nMessage } = getI18n(useIntl());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [selectedRows, setSelectedRows] = useState([]);
  const [filterByName, setFilterByName] = useState('');
  const [filterByTag, setFilterByTag] = useState([]);
  const [changeMode, setChangeMode] = useState(CHANGE_MODE_REMOVE_ACCESS);
  const [currentRows, setCurrentRows] = useState([]);
  const emptyFunction = () => {};

  useEffect(() => {
    if (currentTab === CATEGORY_ORGANIZATIONS_TAB) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      setSelectedRows([]);
      fetchListWithFilters({ accessFilter: ASSIGNED });
    }
  }, [currentTab]);

  const getListColumns = ({ getI18nMessage }) => [{
    id: 'organizationName',
    label: getI18nMessage('label.category.details.orgs.columns.orgname'),
    minWidth: 200,
    format: ({ organizationName, organizationUuid } = {}) => (<Link underline="none" href={`/admin/console/organizations/details/${organizationUuid}`}>
      {organizationName}
    </Link>),
    value: ({ organizationName, organizationUuid } = {}) =>
                  ({ organizationName, organizationUuid }),
  },{
    id: 'organizationTags',
    label: getI18nMessage('label.application.details.apis.tags.column.title'),
    minWidth: 300,
    value: (item) => (
      <div className={classes.tagsGroupContainer}>
        <TagsGroup
          onTagClick={(tag) => onTagClick(tag, item.tags)}
          tags={item.organizationTags}
        />
      </div>
    ),
  },{
    id: 'categoryOrgAccessStatus',
    label: getI18nMessage('label.api.details.apiproducts.list.access.title'),
    minWidth: 50,
    // eslint-disable-next-line react/prop-types
    format: (item) =>
      (<Switch
        id={`${item.organizationUuid}-access`}
        onChange={(val) => onAccessToggle(val, item.organizationUuid)}
        checked={item.categoryOrgAccessStatus === 'ASSIGNED'}

      />
    ),
    value: (item) => (item),
  }];

  useEffect(() => {
    const results = get(categoryOrgs, 'results', []);
    const categoryOrgsClone = cloneDeep(results);
    setCurrentRows(categoryOrgsClone.map(categoryOrg => {
      return { ...categoryOrg, uuid: categoryOrg.organizationUuid }
    }));
  }, [categoryOrgs]);

  useEffect(() => {
    if(currentTab) {
      if (isUpdateSuccess === 'SUCCESS') {
        const message = getI18nMessage('label.category.details.orgs.save.success');
        notifyMessages(ALERT_SUCCESS, message);
        setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
        setPage(0);
        setSelectedRows([]);
        setFilterByName('');
        setFilterByTag([]);
        fetchListWithFilters({ accessFilter: ASSIGNED, name: '', tags: [] });
        setTimeout(() => {
          notifyMessages('', '');
        }, 2500);
      } else if(isUpdateSuccess === 'ERROR') {
        const validationError =
            getFirstValidationError(validationErrors, getI18nMessage('label.category.details.orgs.save.error'));
        setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
        setPage(0);
        setSelectedRows([]);
        fetchListWithFilters({ accessFilter: ASSIGNED });
        notifyMessages(ALERT_ERROR, validationError);
      }
    }
  }, [isUpdateSuccess]);

  const isFormDirty = useMemo(() => {
    return currentRows.some(row => row.categoryOrgAccessStatus !== changeMode);
  }, [currentRows]);

  useEffect(() => {
    if (currentTab === 'orgs-tab') {
      handleOrgsChange(isFormDirty);
    }
  }, [isFormDirty]);

  const onAccessToggle = (val, uuid) => {
    setCurrentRows(currentRows.map(row => {
      if(row.organizationUuid === uuid) {
        return {
          ...row,
          categoryOrgAccessStatus: val ? ASSIGNED : NOT_ASSIGNED,
        }
      } else {
        return row;
      }
    }));
  };
  const fetchListWithFilters = ({
    accessFilter,
    name = filterByName,
    pageNum = page,
    rowsPerPageArg = rowsPerPage,
    tags = filterByTag,
  } = { }) => {
    if(categoryUuid) {
      fetchCategoryAccessOrgs({
        categoryUuid: categoryUuid,
        filterByName : name,
        hasAccess: accessFilter ?
                accessFilter === CHANGE_MODE_REMOVE_ACCESS :
                changeMode === CHANGE_MODE_REMOVE_ACCESS,
        page: pageNum,
        rowsPerPage: rowsPerPageArg,
        tags,
      });
    }
  };

  const onTagsSelect = (tags) => {
    setPage(0);
    setFilterByTag(uniq(tags));
    fetchListWithFilters({ tags: tags, pageNum: 0 });
  };
  const onTagClick = (tag, apiTags) => {
    onTagsSelect(uniq([...filterByTag, tag]), apiTags);
  };
  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(0);
      fetchListWithFilters({ pageNum: 0 });
    }
  };
  
  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchListWithFilters({ pageNum: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchListWithFilters({ pageNum: 0, rowsPerPageArg : newRowsPerPage });
  };

  const addButtonLabel = getI18nMessage('label.category.details.orgs.add.orgs');

  const showAddBtn = changeMode === CHANGE_MODE_REMOVE_ACCESS;
  const onAddOrganization = () => {
    if(currentTab === CATEGORY_ORGANIZATIONS_TAB) {
      fetchListWithFilters({ accessFilter: CHANGE_MODE_ADD_ACCESS });
      clearFilter();
      setChangeMode(CHANGE_MODE_ADD_ACCESS);
    }
  };

  const clearFilter = () => {
    setFilterByName('');
    setPage(0);
    setFilterByTag([]);
  }

  const hasSelectedRowAvailable = (access) => {
    let hasIndex = false;
    selectedRows.forEach((uuid) => {
      const index = findIndex(currentRows,
        item => item.organizationUuid === uuid && item.categoryOrgAccessStatus === access);
      if(index > -1) {
        hasIndex = true;
      }
    });
    return hasIndex;
  }

  const onBulkUpdate = (access) => {
    setCurrentRows(currentRows.map(row => {
      if(selectedRows.includes(row.organizationUuid)) {
        return {
          ...row,
          categoryOrgAccessStatus: access,
        };
      } else {
        return row;
      }
    }));
  };

  const onFilterByTagChange = (value) => {
    setFilterByTag(value);
    setPage(0);
    fetchListWithFilters({
      tags: value,
    });
  };

  const onSave = () => {
    const updatedRows = currentRows.filter(
      row => row.categoryOrgAccessStatus !== changeMode).map(row => row.organizationUuid);
    updateCategoryOrgAccess({
        categoryUuid,
        data: updatedRows,
        action: changeMode === CHANGE_MODE_ADD_ACCESS ? 'ADD' : 'REMOVE',
      });
  }
  const onCancel = () => {
    if(changeMode === CHANGE_MODE_ADD_ACCESS) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      clearFilter();
    } else if(selectedRows.length > 0) {
      setSelectedRows([]);
    }
    fetchListWithFilters({ accessFilter: CHANGE_MODE_REMOVE_ACCESS, page: 0, name: '', tags: [] });
  }

  const totalElements = get(categoryOrgs, 'totalElements', 0);
  const totalPages = get(categoryOrgs, 'totalPages', 0);

  return (
    <CategoryOrgListContext.Provider >
      <Fragment>
        <ListContainer
          hasPaginationDisabled={isFormDirty}
          listPageId="app-apis-list"
          isLoading={false}
          notificationId="request-notifications"
          notificationStatus={''}
          setNotificationStatus={() => {}}
          notificationMessage={''}
          setNotificationMessage={() => {}}
          pageHeaderTitle={''}
          pageHeaderDescription={''}
          pageHeaderTooltipTitle={getI18nMessage('label.request.list.page.title.help')}
          addButtonLabel={''}
          onAdd={false}
          showListHeader={true}
          filterAndSortContent={(
            <Fragment>
                <FilterByName
                  fieldContainerClass={classes.fieldContainer}
                  handleChange={setFilterByName}
                  name={getI18nMessage('label.filter')}
                  onKeyPress={onFilterByNameKeyPress}
                  placeholder={getI18nMessage('label.category.details.orgs.filter.orgs')}
                  value={filterByName}
                />
                <FilterByTag
                  {...props}
                  config={config}
                  fieldContainerClass={`${classes.fieldContainer} ${classes.fieldContainerSelect}`}
                  name={getI18nMessage('label.filter')}
                  value={filterByTag}
                  placeholder={getI18nMessage('label.gateway.bundle.list.page.filter.by.group.placeholder')}
                  handleChange={onFilterByTagChange}
                  hideLabel
                />
                <FilterBySeparator />
                {showAddBtn ?
                  <Grid item md={3} sm={3} xs={12}>
                    <Button
                    className={classes.addBtn}
                    data-apim-test={'app-apis-add'}
                    data-layer7-test={'app-apis-add'}
                    disabled={isFormDirty}
                    id={'app-apis-add'}
                    onClick={onAddOrganization}
                    variant="contained" color="secondary"
                  >
                    {addButtonLabel}
                  </Button>
                </Grid> :
                <FilterBySeparator />}
            </Fragment>
          )}
          columns={getListColumns({ getI18nMessage, classes })}
          rows={currentRows}
          noResultsMessage={getI18nMessage('label.no.results')}
          page={page}
          totalElements={totalElements}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onChangePage={onChangePage}
          onChangePreviousPage={onChangePreviousPage}
          onChangeNextPage={onChangeNextPage}
          notifyMessages={emptyFunction}
          pageClass={classes.listContent}
          pageBodyClass={classes.pageBodyClass}
          pageFilterAndSortClass={classes.pageFilterAndSortClass}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
          hasBulkActions={false}
          bulkActionsContent={
            (<HeaderActionButtons
              {...props}
              id={'app-apis-header'}
              fieldContainerClass={classes.fieldContainer}
              bulkActionsItemsCount={selectedRows.length}
              classes={classes}
              onAssign={() => onBulkUpdate(ASSIGNED)}
              onUnassign={() => onBulkUpdate(NOT_ASSIGNED)}
              disableAdd={!hasSelectedRowAvailable(NOT_ASSIGNED)}
              disableRemove={!hasSelectedRowAvailable(ASSIGNED)}
            />)
          }
        />
        {isFormDirty && <FormActionButtons
          isContainerFooter
          isDisabled={false}
          nextText={getI18nMessage('label.save.button')}
          onNextClick={onSave}
          onCancelClick={onCancel}
          id="category-templates-org-access-form-buttons"
        />}
      </Fragment>
    </CategoryOrgListContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  categoryOrgs: getCategoryOrgs(state),
  isUpdateSuccess: getCategoryOrgsUpdateSuccess(state),
  validationErrors: getValidationErrors(state),
  config: getConfig(state),
});

CategoryOrganizationAccess.propTypes = {
  classes: object,
  fetchCategoryAccessOrgs: func,
  updateCategoryOrgAccess: func,
  categoryUuid: string,
  validationErrors: arrayOf(object),
  config: object,
  currentTab: string,
  categoryOrgs: arrayOf(object),
  notifyMessages: func,
  isUpdateSuccess: string,
  handleOrgsChange: func,
};
CategoryOrganizationAccess.displayName = 'CategoryOrganizationAccess';

const mapDispatchToProps = {
  fetchCategoryAccessOrgs,
  updateCategoryOrgAccess,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(CategoryOrganizationAccess);
